/**
 * EXAMPLE CONTENT — every field of the template's example record, Item. A
 * project replaces this with its own records' fields. `dictionary.test.ts`
 * fails until every field of every main record is described here.
 *
 * Write the description for someone who has never seen the app: what the
 * value means, where it comes from, and anything surprising about it.
 */
import type { FieldDef } from '../types.ts'
import {
  communicationFieldDefs,
  inspectionFieldDefs,
  invoiceFieldDefs,
} from './majik-billing-fields.ts'
import {
  cleanerFieldDefs,
  clientFieldDefs,
  contactFieldDefs,
  locationFieldDefs,
} from './majik-people-fields.ts'
import {
  jobFieldDefs,
  orderFieldDefs,
  productFieldDefs,
  recurringJobFieldDefs,
} from './majik-work-fields.ts'

export const itemFieldDefs: readonly FieldDef[] = [
  {
    entity: 'item',
    key: 'id',
    label: 'Item ID',
    description: 'The app’s own id for the item. Never shown to people; links and AI apps use it.',
    kind: 'id',
    // An id says nothing about the item — it is how everything else finds it.
    tag: 'general',
  },
  {
    entity: 'item',
    key: 'title',
    label: 'Title',
    description: 'What the piece of work is, in a few words. Required.',
    kind: 'text',
    tag: 'general',
  },
  {
    entity: 'item',
    key: 'description',
    label: 'Description',
    description: 'Any detail worth keeping with it. Can be empty.',
    kind: 'text',
    tag: 'general',
  },
  {
    entity: 'item',
    key: 'status',
    label: 'Status',
    description:
      'Where the work stands. The codes and what each means are on the Codes tab; Archived items are kept but leave the Projects list.',
    kind: 'code',
    tag: 'general',
  },
  {
    entity: 'item',
    key: 'createdAt',
    label: 'Created',
    description: 'When the item was first saved. Set once by the database and never changed.',
    kind: 'datetime',
    tag: 'general',
  },
  {
    entity: 'item',
    key: 'updatedAt',
    label: 'Last updated',
    description:
      'When a real value of the item last changed. Kept by the database: saving it unchanged doesn’t move it.',
    kind: 'datetime',
    tag: 'general',
  },
  {
    entity: 'item',
    key: 'createdBy',
    label: 'Created by',
    description:
      'The person who created it. Empty for items whose creator is unknown or whose account was deleted — the item belongs to the workspace and outlives them.',
    kind: 'person',
    tag: 'personal',
  },
  {
    entity: 'item',
    key: 'deletedAt',
    label: 'Deleted',
    description:
      'When it was moved to Recently deleted, or empty while it is live. Restoring empties it again; only Delete forever removes the item.',
    kind: 'datetime',
    tag: 'general',
  },
]

/** Every field the app describes: the example's and Majik's (majik-*-fields.ts). */
export const fieldDefs: readonly FieldDef[] = [
  ...itemFieldDefs,
  ...contactFieldDefs,
  ...clientFieldDefs,
  ...locationFieldDefs,
  ...cleanerFieldDefs,
  ...recurringJobFieldDefs,
  ...jobFieldDefs,
  ...productFieldDefs,
  ...orderFieldDefs,
  ...invoiceFieldDefs,
  ...inspectionFieldDefs,
  ...communicationFieldDefs,
]
